import React, { useState } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Button, FormControl, FormLabel, Input, VStack, HStack, Switch, IconButton, Icon,
    Divider, Text, Box,
} from '@chakra-ui/react';
import { LuPlus, LuTrash2 } from 'react-icons/lu';
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor';
import TypeChipGroup from './TypeChipGroup';
import { DEFAULT_DOC_TYPE_ID } from './sdkDocsModel';

const DEFAULT_VARIANTS = ['Android', 'iOS'];

const AddPlatformModal = ({ isOpen, onClose, onSave, saving }) => {
    const [label, setLabel] = useState('');
    const [heading, setHeading] = useState('');
    const [description, setDescription] = useState('');
    const [version, setVersion] = useState('');
    const [authToken, setAuthToken] = useState('');
    const [tagColor, setTagColor] = useState('#718096');
    const [hasVariants, setHasVariants] = useState(false);
    const [variantLabels, setVariantLabels] = useState(DEFAULT_VARIANTS);
    const [typeId, setTypeId] = useState(DEFAULT_DOC_TYPE_ID);

    const reset = () => {
        setLabel('');
        setHeading('');
        setDescription('');
        setVersion('');
        setAuthToken('');
        setTagColor('#718096');
        setHasVariants(false);
        setVariantLabels(DEFAULT_VARIANTS);
        setTypeId(DEFAULT_DOC_TYPE_ID);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const updateVariantLabel = (index, value) => {
        setVariantLabels(prev => prev.map((v, i) => i === index ? value : v));
    };

    const addVariantRow = () => setVariantLabels(prev => [...prev, '']);
    const removeVariantRow = (index) => setVariantLabels(prev => prev.filter((_, i) => i !== index));

    const handleSave = () => {
        if (!label.trim()) return;
        onSave({
            label: label.trim(),
            heading: heading.trim(),
            description: description.trim(),
            version: version.trim(),
            auth_token: authToken.trim(),
            tag_color: tagColor,
            type_id: typeId,
            hasVariants,
            variantLabels: hasVariants ? variantLabels.map(v => v.trim()).filter(Boolean) : [],
        });
        reset();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="lg" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader fontSize="md">Add Platform</ModalHeader>
                <ModalCloseButton />

                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <FormControl isRequired>
                            <FormLabel fontSize="sm">Label</FormLabel>
                            <Input
                                size="sm"
                                value={label}
                                onChange={e => setLabel(e.target.value)}
                                placeholder="e.g. Unity"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel fontSize="sm">Main Heading</FormLabel>
                            <Input
                                size="sm"
                                value={heading}
                                onChange={e => setHeading(e.target.value)}
                                placeholder="e.g. Unity SDK Integration"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel fontSize="sm">Description</FormLabel>
                            <RichTextEditor
                                value={description}
                                onChange={setDescription}
                                placeholder="e.g. This document provides step-by-step instructions to integrate and initialize the SDK in your Unity application."
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel fontSize="sm">Min Version</FormLabel>
                            <Input
                                size="sm"
                                value={version}
                                onChange={e => setVersion(e.target.value)}
                                placeholder="e.g. 2022.3"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel fontSize="sm">Auth Token</FormLabel>
                            <Input
                                size="sm"
                                fontFamily="mono"
                                fontSize="xs"
                                value={authToken}
                                onChange={e => setAuthToken(e.target.value)}
                                placeholder="jp_xxx... (optional)"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel fontSize="sm">Tag Color</FormLabel>
                            <HStack spacing={2}>
                                <Input
                                    type="color"
                                    size="sm"
                                    value={tagColor}
                                    onChange={e => setTagColor(e.target.value)}
                                    w="40px"
                                    p={1}
                                    cursor="pointer"
                                />
                                <Input
                                    size="sm"
                                    value={tagColor}
                                    onChange={e => setTagColor(e.target.value)}
                                    fontFamily="mono"
                                    fontSize="xs"
                                    w="120px"
                                />
                            </HStack>
                        </FormControl>

                        <Divider />

                        <Box>
                            <Text fontSize="sm" fontWeight="500" color="gray.700" mb={1}>
                                Documentation Type
                            </Text>
                            <Text fontSize="xs" color="gray.500" mb={2}>
                                Pick the one this platform's documentation covers.
                            </Text>
                            <TypeChipGroup value={typeId} onChange={setTypeId} isDisabled={saving} />
                        </Box>

                        <Divider />

                        <FormControl display="flex" alignItems="center" justifyContent="space-between">
                            <FormLabel fontSize="sm" mb={0}>
                                Has OS Variants (e.g. Android / iOS)
                            </FormLabel>
                            <Switch
                                isChecked={hasVariants}
                                onChange={e => setHasVariants(e.target.checked)}
                                colorScheme="blue"
                            />
                        </FormControl>

                        {hasVariants && (
                            <VStack align="stretch" spacing={2} pl={3} borderLeft="2px solid" borderColor="gray.100">
                                {variantLabels.map((v, i) => (
                                    <HStack key={i}>
                                        <Input
                                            size="sm"
                                            value={v}
                                            onChange={e => updateVariantLabel(i, e.target.value)}
                                            placeholder="Variant label"
                                        />
                                        <IconButton
                                            size="xs"
                                            icon={<Icon as={LuTrash2} />}
                                            variant="ghost"
                                            colorScheme="red"
                                            aria-label="Remove variant"
                                            onClick={() => removeVariantRow(i)}
                                        />
                                    </HStack>
                                ))}
                                <Button
                                    size="xs"
                                    leftIcon={<Icon as={LuPlus} />}
                                    variant="outline"
                                    onClick={addVariantRow}
                                    alignSelf="flex-start"
                                >
                                    Add Variant
                                </Button>
                            </VStack>
                        )}
                    </VStack>
                </ModalBody>

                <ModalFooter gap={2}>
                    <Button variant="ghost" size="sm" onClick={handleClose}>Cancel</Button>
                    <Button
                        colorScheme="blue"
                        size="sm"
                        onClick={handleSave}
                        isLoading={saving}
                        isDisabled={!label.trim()}
                    >
                        Add Platform
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default AddPlatformModal;
